import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PaymentsSummary } from "@/components/PaymentsSummary";
import { Card } from "@/components/ui/card";
import { Users, TrendingUp, FileWarning, Mail, Building2, Layers, ArrowRight } from "lucide-react";

const STAT_META = [
  { key: "total_clients", label: "Clients", icon: Users },
  { key: "avg_score", label: "Avg Credit Score", icon: TrendingUp },
  { key: "open_disputes", label: "Open Disputes", icon: FileWarning },
  { key: "letters_generated", label: "Letters Generated", icon: Mail },
  { key: "entities", label: "Business Entities", icon: Building2 },
  { key: "tradelines", label: "Tradelines", icon: Layers },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase text-primary font-bold mb-2">Command Center</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none">Dashboard</h1>
        <p className="text-muted-foreground mt-3">Your full pipeline — from credit repair to corporate credit.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {STAT_META.map(({ key, label, icon: Icon }) => (
          <Card key={key} className="p-5 bg-card/60 border-border hover:-translate-y-1 transition-transform duration-200" data-testid={`stat-${key}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs tracking-widest uppercase text-muted-foreground font-semibold">{label}</span>
              <Icon className="h-4 w-4 text-primary" />
            </div>
            <div className="font-display text-3xl font-bold mt-3">{stats ? stats[key] : "—"}</div>
          </Card>
        ))}
      </div>

      {user && user.role !== "agent" && <PaymentsSummary />}

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Card className="lg:col-span-2 p-6 bg-card/60 border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-semibold">Recent Clients</h3>
            <Link to="/clients" className="text-sm text-primary flex items-center gap-1 hover:gap-2 transition-all">View all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="space-y-2">
            {stats?.recent_clients?.length ? (
              stats.recent_clients.map((c) => (
                <Link to={`/clients/${c.id}`} key={c.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors" data-testid={`recent-client-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm">{c.name?.[0]?.toUpperCase()}</div>
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.email || "No email"}</div>
                    </div>
                  </div>
                  <span className="font-mono text-sm font-semibold">{c.credit_score || "—"}</span>
                </Link>
              ))
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">No clients yet. Add your first client to get started.</p>
            )}
          </div>
        </Card>

        <Card className="p-6 bg-card/60 border-border">
          <h3 className="font-display text-xl font-semibold mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <Link to="/clients" className="block p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors" data-testid="quick-add-client">
              <div className="font-medium text-sm flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Manage Clients</div>
              <div className="text-xs text-muted-foreground mt-1">Add clients & generate dispute letters</div>
            </Link>
            <Link to="/business" className="block p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors" data-testid="quick-business">
              <div className="font-medium text-sm flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Set up a C-Corp / S-Corp</div>
              <div className="text-xs text-muted-foreground mt-1">Guided steps + document generation</div>
            </Link>
            <Link to="/tradelines" className="block p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors" data-testid="quick-tradelines">
              <div className="font-medium text-sm flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> Add Tradelines</div>
              <div className="text-xs text-muted-foreground mt-1">Provider directory & client tradelines</div>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
